import React, { useEffect, useMemo, useRef } from 'react';

let popoverIdCounter = 0;

export type SettingsEditorPopoverProps = {
	isOpen: boolean;
	title: string;
	children: React.ReactNode;
	onCancel: () => void;
	onSave: () => void | Promise<void>;
	isSaving?: boolean;
	saveLabel?: string;
	cancelLabel?: string;
};

export function SettingsEditorPopover({
	isOpen,
	title,
	children,
	onCancel,
	onSave,
	isSaving = false,
	saveLabel = 'Save Changes',
	cancelLabel = 'Cancel',
}: SettingsEditorPopoverProps) {
	const lastActiveRef = useRef<Element | null>(null);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const labelledById = useMemo(() => {
		popoverIdCounter += 1;
		return `settings-editor-popover-${popoverIdCounter}`;
	}, []);

	useEffect(() => {
		if (!isOpen) return undefined;
		lastActiveRef.current = document.activeElement;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !isSaving) onCancel();
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, isSaving, onCancel]);

	useEffect(() => {
		if (!isOpen && lastActiveRef.current instanceof HTMLElement) {
			lastActiveRef.current.focus();
			lastActiveRef.current = null;
		}
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-700/50 backdrop-blur-sm flex justify-center items-center z-[1001]"
			role="dialog"
			aria-modal="true"
			aria-labelledby={labelledById}
		>
			<div className="bg-neutral-600 border-2 border-neutral-400 rounded-large shadow-large max-w-2xl w-full max-h-[80vh] mx-4 flex flex-col">
				<div className="flex justify-between items-center p-md border-b border-neutral-400">
					<h3
						id={labelledById}
						className="text-lg font-medium text-neutral-100 m-0"
					>
						{title}
					</h3>
					<button
						onClick={onCancel}
						className="settings-action-btn"
						type="button"
						aria-label="Close editor"
						disabled={isSaving}
					>
						<i className="fas fa-times" />
					</button>
				</div>
				<div className="p-md flex-1 overflow-y-auto">{children}</div>
				<div className="flex justify-end gap-sm p-md border-t border-neutral-400">
					<button
						onClick={onCancel}
						className="btn-ghost"
						type="button"
						disabled={isSaving}
					>
						{cancelLabel}
					</button>
					<button
						onClick={onSave}
						className="btn-primary"
						type="button"
						disabled={isSaving}
					>
						{isSaving ? 'Saving…' : saveLabel}
					</button>
				</div>
			</div>
		</div>
	);
}
